import { DateTime } from 'luxon';

// Events are always Argentina-local. Pinning the zone (instead of relying on
// the runtime's default) keeps the output identical on the server (Cloud Run
// runs in UTC, which shifted SSR'd hours by +3) and in the browser, so server
// components and hydrated client cards render the same string.
const AR_TIMEZONE = 'America/Argentina/Buenos_Aires';

const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

const parseFriendlyDate = (isoDate: string, capitalize?: boolean, time?: boolean) => {
  const date = DateTime.fromISO(isoDate, { zone: AR_TIMEZONE }).setLocale('es');
  const dayOfWeek = date.toFormat('cccc'); // Obtiene el día de la semana con la primera letra en mayúscula
  const { day } = date; // Obtiene el día del mes sin ceros a la izquierda
  const month = date.toFormat('LLLL'); // Obtiene el nombre completo del mes
  const hour = date.toFormat('HH:mm'); // Obtiene la hora

  if (time) {
    return `${capitalize ? capitalizeFirstLetter(dayOfWeek) : dayOfWeek} ${day} de ${month} - ${hour}hs`;
  }

  return `${capitalize ? capitalizeFirstLetter(dayOfWeek) : dayOfWeek} ${day} de ${month}`;
};

const parseFriendlyDateAndHour = (isoDate: string, withHour?: boolean, withYear?: boolean, useLocalTimezone?: boolean) => {
  if (useLocalTimezone) {
    const date = DateTime.fromISO(isoDate, { zone: 'utc' });
    if (withHour && withYear) {
      return date.setLocale('es').toFormat('dd/MM/yyyy - HH:mm');
    }
    if (withYear) {
      return date.setLocale('es').toFormat('dd/MM/yyyy');
    }
    if (withHour) {
      return date.setLocale('es').toFormat('dd/MM/yyyy - HH:mm');
    }
    return date.setLocale('es').toFormat('dd/MM/yyyy');
  }

  if (withHour && withYear) {
    return DateTime.fromISO(isoDate).setLocale('es').toFormat('dd/MM/yy - HH:mm');
  }
  if (withYear) {
    return DateTime.fromISO(isoDate).setLocale('es').toFormat('dd/MM/yy');
  }
  if (withHour) {
    return DateTime.fromISO(isoDate).setLocale('es').toFormat('dd/MM - HH:mm');
  }
  return DateTime.fromISO(isoDate).setLocale('es').toFormat('dd/MM');
};

const formatDateYYYYMMDD = (date: string | Date) => {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const formatDateForInput = (dateString: string | null) => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  const offset = date.getTimezoneOffset();
  const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
  return adjustedDate.toISOString().slice(0, 16);
};

const combineDateAndTime = (date: string, time: string) => {
  const [year, month, day] = date.split('-');
  const [hours, minutes] = time.split(':');
  const combinedDate = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    parseInt(hours, 10),
    parseInt(minutes, 10),
  );
  return combinedDate.toISOString();
};

const formatDateForTable = (isoDate: string) => {
  if (!isoDate) {
    return '';
  }
  return DateTime.fromISO(isoDate).setLocale('es').toFormat('dd/MM/yyyy - HH:mm');
};

// Dashboard reports: calendar days in the UI, instants in the API so the
// backend filters 00:00–23:59:59.999 of the Argentine day. Pinned to
// AR_TIMEZONE rather than the device zone: the dashboard has to report the same
// totals from any laptop, and this is exactly what the backend derives on its
// own from a bare YYYY-MM-DD (parseStartInstant/parseEndInstant, UTC-3).
const toArgentinaReportBounds = (startDate: string, endDate: string) => {
  if (startDate.includes('T') || endDate.includes('T')) {
    return { start: startDate, end: endDate };
  }
  const start = DateTime.fromISO(startDate, { zone: AR_TIMEZONE }).startOf('day').toISO();
  const end = DateTime.fromISO(endDate, { zone: AR_TIMEZONE }).endOf('day').toISO();
  return {
    start: start ?? startDate,
    end: end ?? endDate,
  };
};

export {
  parseFriendlyDate,
  parseFriendlyDateAndHour,
  formatDateYYYYMMDD,
  formatDateForInput,
  combineDateAndTime,
  formatDateForTable,
  toArgentinaReportBounds,
  AR_TIMEZONE,
};
